"use client"
import React, { useEffect, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { MdClose } from 'react-icons/md';

interface ImageLightboxProps {
    imageUrl: string;
    isOpen: boolean;
    onClose: () => void;
}

export const ImageLightbox: React.FC<ImageLightboxProps> = ({ imageUrl, isOpen, onClose }) => {
    const [isImageLoaded, setIsImageLoaded] = useState(false);

    useEffect(() => {
        setIsImageLoaded(false);
    }, [imageUrl, isOpen]);

    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (!isOpen) return;
        
        if (e.key === 'Escape') {
            onClose();
        }
    }, [isOpen, onClose]);

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, handleKeyDown]);

    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-sm"
            onClick={handleBackdropClick}
        >
            {/* Close button */}
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                }}
                className="absolute top-4 right-4 z-[10000] p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all duration-200 backdrop-blur-sm hover:scale-110"
                aria-label="Close"
                type="button"
            >
                <MdClose size={24} />
            </button>

            {/* Image container */}
            <div className="relative w-full h-full flex items-center justify-center p-4 md:p-8 pointer-events-none">
                <div className="relative max-w-7xl max-h-full pointer-events-auto">
                    {!isImageLoaded && (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-12 h-12 border-4 border-white/20 border-t-white rounded-full animate-spin"></div>
                        </div>
                    )}
                    <img
                        src={imageUrl}
                        alt="Image preview"
                        className={`max-w-full max-h-[calc(100vh-8rem)] object-contain rounded-lg shadow-2xl transition-opacity duration-300 ${
                            isImageLoaded ? 'opacity-100' : 'opacity-0'
                        }`}
                        onLoad={() => setIsImageLoaded(true)}
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            </div>

            {/* Keyboard hint */}
            <div className="hidden md:block absolute bottom-4 left-1/2 -translate-x-1/2 z-10 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-white text-xs">
                <span className="opacity-75">
                    Press <kbd className="px-2 py-1 bg-white/20 rounded">ESC</kbd> to close or click outside
                </span>
            </div>
        </div>,
        document.body
    );
};

